import React, { useCallback, useEffect, useState } from "react";
import Card from "components/card";
import Body, { ExtendedBodyPart, Slug } from "components/react-body-highlighter";
import {
  BODY_LOCATIONS_DATA,
  BILATERAL_SLUGS,
  buildLabel,
  getSublocationsForLabel,
} from "./SelectLocationModal";
import { deleteDataById, fetchData, insertData, updatetDataById } from "api";
import { locations } from "api/collection_names";
import { SnackbarState } from "models/user-models";
import AppSnackbar from "components/snakbar";
import { PiCameraRotateLight, PiGenderMale, PiGenderFemale } from "react-icons/pi";
import { LiaSearchLocationSolid } from "react-icons/lia";
import { MdSave, MdRefresh } from "react-icons/md";
import { BsPlusLg, BsArrowUp, BsArrowDown, BsTrash } from "react-icons/bs";

export interface ScrollDown {
  description: string;
  priority: number;
}

export interface LocationType {
  _id?: string;
  muscle_id?: string;
  gender?: "male" | "female";
  side?: "front" | "back";
  description: string;
  priority: number;
  system_id?: string[];
  scroll_downs?: ScrollDown[];
}

const Locations = (): any => {
  const [gender, setGender] = useState<"male" | "female">("male");
  const [site, setSite] = useState<"Front" | "Back">("Front");
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [selectedMuscleId, setSelectedMuscleId] = useState<string | null>(null);
  const [bodyData, setBodyData] = useState<(ExtendedBodyPart & { bodySide?: "front" | "back" })[]>([]);

  const [dbLocations, setDbLocations] = useState<LocationType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [listSideFilter, setListSideFilter] = useState<"all" | "front" | "back">("all");
  const [resetConfirmOpen, setResetConfirmOpen] = useState<boolean>(false);

  // Active location editor state
  const [activeLabel, setActiveLabel] = useState<string>("");
  const [activeSublocations, setActiveSublocations] = useState<ScrollDown[]>(
    []
  );
  const [newSublocationInput, setNewSublocationInput] = useState<string>("");

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  /**
   * =========================================================================
   * UTILITY FUNCTION: Reset & Bulk Upload Body Locations to DB
   * =========================================================================
   * Deletes ALL existing location records from the backend database,
   * then re-inserts isolated datasets for male & female body models,
   * keeping Front and Back body parts completely separate.
   */
  const resetAndBulkUploadLocations = async () => {
    try {
      setLoading(true);
      console.log("Starting bulk reset of Locations DB collection with isolated Front/Back Male and Female datasets...");

      // 1. Fetch system IDs to link body locations to all systems
      let systemIds: string[] = [];
      try {
        const sysRes = await fetchData("Systems");
        if (sysRes.success && Array.isArray(sysRes.data)) {
          systemIds = sysRes.data.map((sys: any) => sys._id);
        }
      } catch (e) {
        console.warn("Could not fetch system IDs for locations link", e);
      }

      // 2. Delete all current location records from backend in batches of 15
      const res = await fetchData(locations);
      if (res.success && Array.isArray(res.data) && res.data.length > 0) {
        console.log(`Deleting ${res.data.length} existing records from DB...`);
        const deleteIds = res.data.filter((item: any) => item._id).map((item: any) => item._id);
        for (let i = 0; i < deleteIds.length; i += 15) {
          const chunk = deleteIds.slice(i, i + 15);
          await Promise.all(chunk.map((id: string) => deleteDataById(locations, id)));
        }
        console.log("All existing locations deleted successfully.");
      }

      // 3. Extract ALL body parts from male and female data (Front & Back) with complete Front/Back & Bilateral separation
      const allParts: {
        muscleId: string;
        gender: "male" | "female";
        side: "front" | "back";
        label: string;
        sublocations: string[];
      }[] = [];

      const addGenderParts = (
        targetGender: "male" | "female",
        viewSide: "front" | "back",
        dataObj: Record<string, { label: string; sublocations: string[] }>
      ) => {
        Object.entries(dataObj).forEach(([id, val]) => {
          const slug = id as Slug;
          const isBilateral = BILATERAL_SLUGS.includes(slug);

          if (isBilateral) {
            const leftId = `${targetGender}-${viewSide}-${slug}-left`;
            const leftLabel = `Left ${val.label}`;
            const rightId = `${targetGender}-${viewSide}-${slug}-right`;
            const rightLabel = `Right ${val.label}`;

            allParts.push({ muscleId: leftId, gender: targetGender, side: viewSide, label: leftLabel, sublocations: [...val.sublocations] });
            allParts.push({ muscleId: rightId, gender: targetGender, side: viewSide, label: rightLabel, sublocations: [...val.sublocations] });
          } else {
            const key = `${targetGender}-${viewSide}-${slug}`;
            allParts.push({
              muscleId: key,
              gender: targetGender,
              side: viewSide,
              label: val.label,
              sublocations: [...val.sublocations],
            });
          }
        });
      };

      // Male parts: Front and Back separate
      addGenderParts("male", "front", BODY_LOCATIONS_DATA.male.front);
      addGenderParts("male", "back", BODY_LOCATIONS_DATA.male.back);

      // Female parts: Front and Back separate
      addGenderParts("female", "front", BODY_LOCATIONS_DATA.female.front);
      addGenderParts("female", "back", BODY_LOCATIONS_DATA.female.back);

      console.log(`Uploading ${allParts.length} isolated body location records to DB...`);

      // 4. Bulk insert each body part into DB in batches of 15
      const payloads: LocationType[] = allParts.map((part, i) => ({
        muscle_id: part.muscleId,
        gender: part.gender,
        side: part.side,
        description: part.label,
        priority: i,
        system_id: systemIds,
        scroll_downs: part.sublocations.map((sub, sIdx) => ({
          description: sub,
          priority: sIdx,
        })),
      }));

      for (let i = 0; i < payloads.length; i += 15) {
        const chunk = payloads.slice(i, i + 15);
        await Promise.all(chunk.map((p) => insertData(locations, p)));
      }

      console.log("Bulk upload of isolated male & female front/back body parts completed successfully!");
      setSnackbar({
        open: true,
        message: `Successfully reset body locations to default datasets (${allParts.length} locations)!`,
        severity: "success",
      });

      // 5. Reload location state from database
      await loadLocations();
    } catch (err: any) {
      console.error("Error in resetAndBulkUploadLocations:", err);
      setSnackbar({
        open: true,
        message: "Failed to reset & bulk upload locations",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setResetConfirmOpen(false);
    }
  };

  // Load locations from database
  const loadLocations = async () => {
    setLoading(true);
    try {
      const res = await fetchData(locations);
      if (res.success && Array.isArray(res.data)) {
        if (res.data.length === 0) {
          // If database has no locations at all, seed with default isolated datasets
          await resetAndBulkUploadLocations();
          return;
        }
        setDbLocations(res.data);
      } else {
        setDbLocations([]);
      }
    } catch (err: any) {
      console.error("Error loading locations:", err);
      setSnackbar({
        open: true,
        message: "Failed to load locations from database",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLocations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getDbRecordForMuscle = (
    rawSlug: Slug,
    side: "left" | "right" | undefined,
    targetGender: "male" | "female",
    viewSide: "front" | "back"
  ): LocationType | undefined => {
    let slug = rawSlug;
    if (slug === ("anus" as Slug)) slug = "perineum";
    if (slug === ("female-anus" as Slug)) slug = "female-perineum";
    if (slug === ("spine" as Slug)) slug = "backbone";

    const isBilateral = BILATERAL_SLUGS.includes(slug);
    const effectiveSide = isBilateral ? side : undefined;
    const sideGenderKey = effectiveSide ? `${targetGender}-${viewSide}-${slug}-${effectiveSide}` : `${targetGender}-${viewSide}-${slug}`;
    const legacyGenderKey = effectiveSide ? `${targetGender}-${slug}-${effectiveSide}` : `${targetGender}-${slug}`;
    const baseKey = effectiveSide ? `${slug}-${effectiveSide}` : slug;

    const isOpposite = (loc: LocationType) => {
      if (targetGender === "female" && (loc.gender === "male" || loc.muscle_id?.startsWith("male-"))) return true;
      if (targetGender === "male" && (loc.gender === "female" || loc.muscle_id?.startsWith("female-"))) return true;
      const d = (loc.description || "").toLowerCase();
      if (targetGender === "female" && d.includes("male") && !d.includes("female")) return true;
      if (targetGender === "male" && d.includes("female")) return true;
      return false;
    };

    const isOppositeSide = (loc: LocationType) => {
      if (loc.side && loc.side !== viewSide) return true;
      if (loc.muscle_id) {
        if (viewSide === "front" && (loc.muscle_id.includes("-back-") || loc.muscle_id.startsWith("back-"))) return true;
        if (viewSide === "back" && (loc.muscle_id.includes("-front-") || loc.muscle_id.startsWith("front-"))) return true;
      }
      const genderData = targetGender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;
      const oppositeSideKey = viewSide === "front" ? "back" : "front";
      const descLower = (loc.description || "").toLowerCase();
      const strippedDesc = descLower.replace(/^(left|right)\s+/, "");

      const oppositeMatch = Object.values(genderData[oppositeSideKey]).some(
        (v) => v.label.toLowerCase() === descLower || v.label.toLowerCase() === strippedDesc
      );
      const currentMatch = Object.values(genderData[viewSide]).some(
        (v) => v.label.toLowerCase() === descLower || v.label.toLowerCase() === strippedDesc
      );

      if (oppositeMatch && !currentMatch) return true;

      if (viewSide === "back" && !loc.side && (!loc.muscle_id || (!loc.muscle_id.includes("-back-") && !loc.muscle_id.startsWith("back-")))) {
        const isFrontLabel = Object.values(genderData.front).some(
          (v) => v.label.toLowerCase() === descLower || v.label.toLowerCase() === strippedDesc
        );
        if (isFrontLabel) return true;
      }

      return false;
    };

    // 1. Exact gender-side-prefixed muscle_id match (e.g. "male-front-chest-left" or "female-back-head")
    const genderSideMatch = dbLocations.find(
      (loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.muscle_id === sideGenderKey
    );
    if (genderSideMatch) return genderSideMatch;

    // 2. Exact gender & side field match with base muscle_id or sideGenderKey
    const genderFieldMatch = dbLocations.find(
      (loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.gender === targetGender && loc.side === viewSide && (loc.muscle_id === baseKey || loc.muscle_id === sideGenderKey)
    );
    if (genderFieldMatch) return genderFieldMatch;

    // 3. Default label match for this gender and view side
    const viewSideCapitalized = (viewSide.charAt(0).toUpperCase() + viewSide.slice(1)) as "Front" | "Back";
    const defaultLabel = buildLabel(slug, effectiveSide, dbLocations as any, viewSideCapitalized, targetGender);
    const labelMatch = dbLocations.find(
      (loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.description.toLowerCase() === defaultLabel.toLowerCase()
    );
    if (labelMatch) return labelMatch;

    // 4. Legacy fallback (only if no opposite side indicated)
    const legacyGenderMatch = dbLocations.find(
      (loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.muscle_id === legacyGenderKey
    );
    if (legacyGenderMatch) return legacyGenderMatch;

    if (effectiveSide) {
      const legacySideMatch = dbLocations.find(
        (loc) =>
          !isOpposite(loc) &&
          !isOppositeSide(loc) &&
          loc.muscle_id === slug &&
          loc.description.toLowerCase().startsWith(effectiveSide)
      );
      if (legacySideMatch) return legacySideMatch;
    }

    if (!isBilateral) {
      const legacyBaseMatch = dbLocations.find(
        (loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.muscle_id === slug
      );
      if (legacyBaseMatch) return legacyBaseMatch;
    }

    // Legacy fallback for merged perineum / perianal
    if (slug === "perineum") {
      const legacyAnusMatch = dbLocations.find(
        (loc) =>
          !isOpposite(loc) &&
          !isOppositeSide(loc) &&
          (loc.muscle_id === "male-back-anus" ||
            loc.muscle_id === "anus" ||
            (loc.description && loc.description.toLowerCase().includes("perianal")))
      );
      if (legacyAnusMatch) return legacyAnusMatch;
    } else if (slug === "female-perineum") {
      const legacyFemaleAnusMatch = dbLocations.find(
        (loc) =>
          !isOpposite(loc) &&
          !isOppositeSide(loc) &&
          (loc.muscle_id === "female-back-female-anus" ||
            loc.muscle_id === "female-anus" ||
            (loc.description && loc.description.toLowerCase().includes("perianal")))
      );
      if (legacyFemaleAnusMatch) return legacyFemaleAnusMatch;
    }

    return undefined;
  };

  // Select muscle handler with distinct Left/Right, Gender, and Front/Back support
  const selectMuscle = useCallback(
    (rawSlug: Slug, side?: "left" | "right", viewSide?: "front" | "back") => {
      let slug = rawSlug;
      if (slug === ("anus" as Slug)) slug = "perineum";
      if (slug === ("female-anus" as Slug)) slug = "female-perineum";
      if (slug === ("spine" as Slug)) slug = "backbone";

      const currentViewSide = viewSide || (site.toLowerCase() as "front" | "back");
      const currentSiteCap = (currentViewSide.charAt(0).toUpperCase() + currentViewSide.slice(1)) as "Front" | "Back";

      if (site !== currentSiteCap) {
        setSite(currentSiteCap);
      }

      const isBilateral = BILATERAL_SLUGS.includes(slug);
      const effectiveSide = isBilateral ? (side || "left") : undefined;
      const muscleKey = effectiveSide
        ? `${gender}-${currentViewSide}-${slug}-${effectiveSide}`
        : `${gender}-${currentViewSide}-${slug}`;

      setSelectedMuscleId(muscleKey);

      // Update body highlight
      if (effectiveSide) {
        setBodyData([{ slug, intensity: 1, side: effectiveSide, bodySide: currentViewSide }]);
      } else {
        setBodyData([{ slug, intensity: 1, bodySide: currentViewSide }]);
      }

      const record = getDbRecordForMuscle(slug, effectiveSide, gender, currentViewSide);
      const defaultLabel = buildLabel(slug, effectiveSide, dbLocations as any, currentSiteCap, gender);
      const defaultSublocations = getSublocationsForLabel(defaultLabel, dbLocations as any, gender, currentSiteCap);

      if (record) {
        setActiveLabel(record.description || defaultLabel);
        setActiveSublocations(
          Array.isArray(record.scroll_downs) && record.scroll_downs.length > 0
            ? [...record.scroll_downs].sort((a, b) => a.priority - b.priority)
            : defaultSublocations.map((sub, idx) => ({ description: sub, priority: idx }))
        );
      } else {
        setActiveLabel(defaultLabel);
        setActiveSublocations(
          defaultSublocations.map((sub, idx) => ({
            description: sub,
            priority: idx,
          }))
        );
      }
      setNewSublocationInput("");
    },
    [dbLocations, gender, site] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // When gender switches, update active muscle selection to the corresponding part if applicable
  useEffect(() => {
    if (selectedMuscleId) {
      const isBilateral = selectedMuscleId.endsWith("-left") || selectedMuscleId.endsWith("-right");
      const side = selectedMuscleId.endsWith("-left") ? "left" : selectedMuscleId.endsWith("-right") ? "right" : undefined;
      let cleanId = selectedMuscleId.replace(/^(male|female)-/, "");
      let viewSide: "front" | "back" = "front";
      if (cleanId.startsWith("front-")) {
        viewSide = "front";
        cleanId = cleanId.replace(/^front-/, "");
      } else if (cleanId.startsWith("back-")) {
        viewSide = "back";
        cleanId = cleanId.replace(/^back-/, "");
      }
      if (side) cleanId = cleanId.replace(/-(left|right)$/, "");

      // Handle gender cross-mapping for perineum
      let mappedCleanId = cleanId;
      if (gender === "female" && (mappedCleanId === "perineum" || mappedCleanId === "anus")) {
        mappedCleanId = "female-perineum";
      } else if (gender === "male" && (mappedCleanId === "female-perineum" || mappedCleanId === "female-anus")) {
        mappedCleanId = "perineum";
      }

      const currentGenderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;
      const existsInNewGender = mappedCleanId in currentGenderData[viewSide];

      if (existsInNewGender) {
        selectMuscle(mappedCleanId as Slug, side, viewSide);
      } else {
        setSelectedMuscleId(null);
        setBodyData([]);
        setActiveLabel("");
        setActiveSublocations([]);
      }
    }
  }, [gender]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle switching view side (Front <-> Back)
  const handleToggleSite = () => {
    const nextSite = site === "Front" ? "Back" : "Front";
    const nextViewSide = nextSite.toLowerCase() as "front" | "back";
    setSite(nextSite);

    if (listSideFilter !== "all") {
      setListSideFilter(nextViewSide);
    }

    if (selectedMuscleId) {
      const isBilateral = selectedMuscleId.endsWith("-left") || selectedMuscleId.endsWith("-right");
      const side = selectedMuscleId.endsWith("-left") ? "left" : selectedMuscleId.endsWith("-right") ? "right" : undefined;
      let cleanId = selectedMuscleId.replace(/^(male|female)-/, "");
      if (cleanId.startsWith("front-")) cleanId = cleanId.replace(/^front-/, "");
      else if (cleanId.startsWith("back-")) cleanId = cleanId.replace(/^back-/, "");
      if (side) cleanId = cleanId.replace(/-(left|right)$/, "");

      const currentGenderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;
      const existsInNextSide = cleanId in currentGenderData[nextViewSide];

      if (existsInNextSide) {
        selectMuscle(cleanId as Slug, side, nextViewSide);
      } else {
        setSelectedMuscleId(null);
        setBodyData([]);
        setActiveLabel("");
        setActiveSublocations([]);
      }
    }
  };

  const handleBodyPartClick = useCallback(
    (bodyPart: ExtendedBodyPart, side?: "left" | "right") => {
      if (bodyPart.slug) {
        let slug = bodyPart.slug;
        if (slug === ("anus" as Slug)) slug = "perineum";
        if (slug === ("female-anus" as Slug)) slug = "female-perineum";
        if (slug === ("spine" as Slug)) slug = "backbone";
        selectMuscle(slug, side, site.toLowerCase() as "front" | "back");
      }
    },
    [selectMuscle, site]
  );

  // Sublocation Management Handlers
  const handleAddSublocation = () => {
    if (!newSublocationInput.trim()) return;
    const newSub: ScrollDown = {
      description: newSublocationInput.trim(),
      priority: activeSublocations.length,
    };
    setActiveSublocations([...activeSublocations, newSub]);
    setNewSublocationInput("");
  };

  const handleUpdateSublocationText = (index: number, val: string) => {
    const updated = [...activeSublocations];
    updated[index].description = val;
    setActiveSublocations(updated);
  };

  const handleDeleteSublocation = (index: number) => {
    const filtered = activeSublocations.filter((_, idx) => idx !== index);
    const reindexed = filtered.map((sub, idx) => ({ ...sub, priority: idx }));
    setActiveSublocations(reindexed);
  };

  const handleMoveSublocationUp = (index: number) => {
    if (index === 0) return;
    const updated = [...activeSublocations];
    const temp = updated[index - 1];
    updated[index - 1] = updated[index];
    updated[index] = temp;
    const reindexed = updated.map((sub, idx) => ({ ...sub, priority: idx }));
    setActiveSublocations(reindexed);
  };

  const handleMoveSublocationDown = (index: number) => {
    if (index === activeSublocations.length - 1) return;
    const updated = [...activeSublocations];
    const temp = updated[index + 1];
    updated[index + 1] = updated[index];
    updated[index] = temp;
    const reindexed = updated.map((sub, idx) => ({ ...sub, priority: idx }));
    setActiveSublocations(reindexed);
  };

  // Save changes for selected body part strictly for active gender and view side
  const handleSaveChanges = async () => {
    if (!selectedMuscleId) return;
    if (!activeLabel.trim()) {
      setSnackbar({
        open: true,
        message: "Label description cannot be empty",
        severity: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const isBilateral = selectedMuscleId.endsWith("-left") || selectedMuscleId.endsWith("-right");
      const side = selectedMuscleId.endsWith("-left") ? "left" : selectedMuscleId.endsWith("-right") ? "right" : undefined;
      let cleanId = selectedMuscleId.replace(/^(male|female)-/, "");
      let viewSide: "front" | "back" = site.toLowerCase() as "front" | "back";
      if (cleanId.startsWith("front-")) {
        viewSide = "front";
        cleanId = cleanId.replace(/^front-/, "");
      } else if (cleanId.startsWith("back-")) {
        viewSide = "back";
        cleanId = cleanId.replace(/^back-/, "");
      }
      if (side) cleanId = cleanId.replace(/-(left|right)$/, "");
      const slug = cleanId as Slug;

      const record = getDbRecordForMuscle(slug, side, gender, viewSide);
      const cleanSublocations = activeSublocations.map((sub, idx) => ({
        description: sub.description.trim(),
        priority: idx,
      }));

      const payload: Partial<LocationType> = {
        muscle_id: selectedMuscleId,
        gender: gender,
        side: viewSide,
        description: activeLabel.trim(),
        scroll_downs: cleanSublocations,
      };

      if (record && record._id) {
        const res = await updatetDataById(locations, payload, record._id);
        if (res.success) {
          setDbLocations((prev) =>
            prev.map((item) =>
              item._id === record._id ? { ...item, ...payload } : item
            )
          );
          setSnackbar({
            open: true,
            message: `Updated "${activeLabel}" (${viewSide}) for ${gender} successfully!`,
            severity: "success",
          });
        } else {
          setSnackbar({
            open: true,
            message: res.message || "Failed to update location",
            severity: "error",
          });
        }
      } else {
        const newRecordPayload: LocationType = {
          muscle_id: selectedMuscleId,
          gender: gender,
          side: viewSide,
          description: activeLabel.trim(),
          priority: dbLocations.length,
          system_id: [],
          scroll_downs: cleanSublocations,
        };
        const res = await insertData(locations, newRecordPayload);
        if (res.success) {
          setDbLocations((prev) => [...prev, res.data || newRecordPayload]);
          setSnackbar({
            open: true,
            message: `Saved "${activeLabel}" (${viewSide}) for ${gender} to database!`,
            severity: "success",
          });
        } else {
          setSnackbar({
            open: true,
            message: res.message || "Failed to save location",
            severity: "error",
          });
        }
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setSnackbar({
        open: true,
        message: "An error occurred while saving",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Build full list of body parts for the active gender with separate entries for Front and Back
  const currentGenderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;

  const allMusclesList: {
    muscleId: string;
    slug: Slug;
    side?: "left" | "right";
    viewSide: "front" | "back";
    label: string;
    subCount: number;
  }[] = [];

  const addMusclesFromSide = (
    viewSide: "front" | "back",
    sideData: Record<string, { label: string; sublocations: string[] }>
  ) => {
    const viewSideCap = (viewSide.charAt(0).toUpperCase() + viewSide.slice(1)) as "Front" | "Back";

    Object.entries(sideData).forEach(([mId, defaultVal]) => {
      const slug = mId as Slug;
      const isBilateral = BILATERAL_SLUGS.includes(slug);

      if (isBilateral) {
        // Left side
        const leftKey = `${gender}-${viewSide}-${slug}-left`;
        const leftRecord = getDbRecordForMuscle(slug, "left", gender, viewSide);
        const leftLabel = leftRecord?.description || buildLabel(slug, "left", dbLocations as any, viewSideCap, gender);
        const leftSubCount = leftRecord?.scroll_downs?.length ?? defaultVal.sublocations.length;
        if (!allMusclesList.some((m) => m.muscleId === leftKey)) {
          allMusclesList.push({
            muscleId: leftKey,
            slug,
            side: "left",
            viewSide,
            label: leftLabel,
            subCount: leftSubCount,
          });
        }

        // Right side
        const rightKey = `${gender}-${viewSide}-${slug}-right`;
        const rightRecord = getDbRecordForMuscle(slug, "right", gender, viewSide);
        const rightLabel = rightRecord?.description || buildLabel(slug, "right", dbLocations as any, viewSideCap, gender);
        const rightSubCount = rightRecord?.scroll_downs?.length ?? defaultVal.sublocations.length;
        if (!allMusclesList.some((m) => m.muscleId === rightKey)) {
          allMusclesList.push({
            muscleId: rightKey,
            slug,
            side: "right",
            viewSide,
            label: rightLabel,
            subCount: rightSubCount,
          });
        }
      } else {
        const key = `${gender}-${viewSide}-${slug}`;
        const record = getDbRecordForMuscle(slug, undefined, gender, viewSide);
        const label = record?.description || defaultVal.label;
        const subCount = record?.scroll_downs?.length ?? defaultVal.sublocations.length;
        if (!allMusclesList.some((m) => m.muscleId === key)) {
          allMusclesList.push({
            muscleId: key,
            slug,
            side: undefined,
            viewSide,
            label,
            subCount,
          });
        }
      }
    });
  };

  addMusclesFromSide("front", currentGenderData.front);
  addMusclesFromSide("back", currentGenderData.back);

  const filteredMuscles = allMusclesList.filter((m) => {
    if (listSideFilter !== "all" && m.viewSide !== listSideFilter) {
      return false;
    }
    return (
      m.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.muscleId.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="mt-5 w-full h-full">
      <Card extra={"w-full h-full px-6 py-6"}>
        <header className="border-borderPrimary/60 flex flex-wrap items-center justify-between gap-4 border-b pb-4">
          <div>
            <h1 className="text-2xl font-bold text-textPrimary">
              Body Locations Management
            </h1>
            <p className="text-sm text-textSecondary">
              Click any body region on the model or select from the list to
              customize labels and manage sublocations.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Gender Switch */}
            <div className="flex items-center rounded-lg border border-borderPrimary bg-surfaceSecondary p-1">
              <button
                type="button"
                onClick={() => setGender("male")}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${gender === "male"
                  ? "bg-brand-500 text-white shadow-sm"
                  : "text-textSecondary hover:text-textPrimary"
                  }`}
              >
                <PiGenderMale className="text-base" />
                <span>Male</span>
              </button>
              <button
                type="button"
                onClick={() => setGender("female")}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${gender === "female"
                  ? "bg-brand-500 text-white shadow-sm"
                  : "text-textSecondary hover:text-textPrimary"
                  }`}
              >
                <PiGenderFemale className="text-base" />
                <span>Female</span>
              </button>
            </div>

            {/* Side Switch */}
            <button
              type="button"
              onClick={handleToggleSite}
              className="flex items-center gap-2 rounded-lg border border-borderPrimary bg-surfaceSecondary px-4 py-2 text-sm font-semibold text-brand-500 transition hover:bg-surfaceSecondary/80 active:bg-surfaceSecondary/70"
            >
              <PiCameraRotateLight className="text-lg" />
              <span>Switch to {site === "Front" ? "Back" : "Front"} View</span>
            </button>

            {/* Reset Location Data Button */}
            {/*
            <button
              type="button"
              disabled={loading}
              onClick={() => setResetConfirmOpen(true)}
              className="flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-3.5 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-500/20 active:bg-red-500/30 disabled:opacity-50"
              title="Reset all body locations to default isolated datasets"
            >

              <MdRefresh className="text-base" />
              <span>Reset Location Data</span>
            </button> 
            */}

          </div>
        </header>

        {loading ? (
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-dashed border-brand-500"></div>
            <span className="ml-3 text-base font-semibold text-textPrimary">
              Loading Body Locations...
            </span>
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-stretch gap-6 lg:flex-row">
            {/* Left Side: Body Part Editor & Selector */}
            <div className="flex min-h-[500px] flex-1 flex-col gap-6 overflow-y-auto pr-1">
              {selectedMuscleId ? (
                <div className="bg-surfaceSecondary/40 flex flex-col gap-5 rounded-2xl border border-borderPrimary p-5 shadow-sm">
                  <div className="border-borderPrimary/60 flex items-center justify-between border-b pb-3">
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-textSecondary">
                        Selected Body Region ({selectedMuscleId?.includes("-back-") ? "Back" : "Front"} View)
                      </span>
                      <h2 className="text-xl font-bold text-brand-500">
                        {activeLabel || selectedMuscleId}
                      </h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-brand-500/10 border-brand-500/20 rounded-full border px-3 py-1 text-xs font-semibold text-brand-500">
                        {selectedMuscleId?.includes("-back-") ? "Back" : "Front"}
                      </span>
                    </div>
                  </div>

                  {/* Rename Label */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-semibold text-textPrimary">
                      Region Label (Name)
                    </label>
                    <input
                      type="text"
                      value={activeLabel}
                      onChange={(e) => setActiveLabel(e.target.value)}
                      placeholder="Enter label for this body part"
                      className="w-full rounded-xl border border-borderPrimary bg-surfacePrimary px-4 py-2.5 text-sm text-textPrimary focus:border-brand-500 focus:outline-none"
                    />
                  </div>

                  {/* Sublocations Editor */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-textPrimary">
                        Sublocations / Options ({activeSublocations.length})
                      </label>
                      <span className="text-xs text-textSecondary">
                        (Admin can add, edit or delete sublocations)
                      </span>
                    </div>

                    {/* Sublocations List */}
                    {activeSublocations.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-borderPrimary p-4 text-center text-xs italic text-textSecondary">
                        No sublocations added yet. Use the field below to add
                        one.
                      </p>
                    ) : (
                      <div className="flex max-h-[250px] flex-col gap-2 overflow-y-auto pr-1">
                        {activeSublocations.map((sub, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 rounded-xl border border-borderPrimary bg-surfacePrimary p-2"
                          >
                            <input
                              type="text"
                              value={sub.description}
                              onChange={(e) =>
                                handleUpdateSublocationText(idx, e.target.value)
                              }
                              className="border-transparent bg-transparent flex-1 rounded-lg border px-3 py-1 text-sm text-textPrimary focus:border-brand-500 focus:bg-surfaceSecondary focus:outline-none"
                            />

                            {/* Move Up */}
                            <button
                              type="button"
                              disabled={idx === 0}
                              onClick={() => handleMoveSublocationUp(idx)}
                              className="p-1.5 text-textSecondary hover:text-brand-500 disabled:opacity-30"
                              title="Move Up"
                            >
                              <BsArrowUp />
                            </button>

                            {/* Move Down */}
                            <button
                              type="button"
                              disabled={idx === activeSublocations.length - 1}
                              onClick={() => handleMoveSublocationDown(idx)}
                              className="p-1.5 text-textSecondary hover:text-brand-500 disabled:opacity-30"
                              title="Move Down"
                            >
                              <BsArrowDown />
                            </button>

                            {/* Delete Sublocation */}
                            <button
                              type="button"
                              onClick={() => handleDeleteSublocation(idx)}
                              className="rounded-lg p-1.5 text-red-500 transition hover:bg-red-500/10"
                              title="Delete Sublocation"
                            >
                              <BsTrash />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New Sublocation Input */}
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={newSublocationInput}
                        onChange={(e) => setNewSublocationInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddSublocation();
                          }
                        }}
                        placeholder="Add new sublocation name..."
                        className="flex-1 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 py-2 text-sm text-textPrimary focus:border-brand-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleAddSublocation}
                        className="flex items-center gap-1.5 rounded-xl bg-surfaceSecondary px-4 py-2 text-sm font-semibold text-brand-500 transition hover:bg-surfaceSecondary/80 active:bg-surfaceSecondary/70"
                      >
                        <BsPlusLg /> Add
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="border-borderPrimary/60 mt-2 flex items-center justify-end gap-3 border-t pt-4">
                    <button
                      type="button"
                      disabled={saving}
                      onClick={handleSaveChanges}
                      className="flex items-center gap-2 rounded-xl bg-brand-500 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-500/80 active:bg-brand-500/70 disabled:opacity-50"
                    >
                      <MdSave className="text-lg" />
                      {saving ? "Saving..." : "Save Location Changes"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-surfaceSecondary/30 flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-borderPrimary p-8 text-center">
                  <LiaSearchLocationSolid className="mb-3 text-5xl text-brand-500 opacity-60" />
                  <h3 className="text-lg font-semibold text-textPrimary">
                    Select a Body Region
                  </h3>
                  <p className="mt-1 max-w-sm text-sm text-textSecondary">
                    Click on any region on the {gender} body model (right) or pick
                    from the list below to edit its label and sublocations.
                  </p>
                </div>
              )}

              {/* Body Parts Quick List / Search */}
              <div className="bg-surfaceSecondary/20 flex flex-col gap-3 rounded-2xl border border-borderPrimary p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-textPrimary">
                      {gender === "female" ? "Female" : "Male"} Regions ({filteredMuscles.length})
                    </h3>
                    {/* View Side Filter Tabs */}
                    <div className="flex items-center rounded-lg border border-borderPrimary bg-surfacePrimary p-0.5 text-xs">
                      <button
                        type="button"
                        onClick={() => setListSideFilter("all")}
                        className={`rounded-md px-2 py-0.5 font-medium transition ${listSideFilter === "all"
                          ? "bg-brand-500 text-white shadow-sm"
                          : "text-textSecondary hover:text-textPrimary"
                          }`}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        onClick={() => setListSideFilter("front")}
                        className={`rounded-md px-2 py-0.5 font-medium transition ${listSideFilter === "front"
                          ? "bg-brand-500 text-white shadow-sm"
                          : "text-textSecondary hover:text-textPrimary"
                          }`}
                      >
                        Front
                      </button>
                      <button
                        type="button"
                        onClick={() => setListSideFilter("back")}
                        className={`rounded-md px-2 py-0.5 font-medium transition ${listSideFilter === "back"
                          ? "bg-brand-500 text-white shadow-sm"
                          : "text-textSecondary hover:text-textPrimary"
                          }`}
                      >
                        Back
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search body part..."
                    className="rounded-lg border border-borderPrimary bg-surfacePrimary px-3 py-1 text-xs text-textPrimary focus:border-brand-500 focus:outline-none"
                  />
                </div>

                <div className="flex max-h-[160px] flex-wrap gap-2 overflow-y-auto pr-1">
                  {filteredMuscles.map((m) => {
                    const isSelected = selectedMuscleId === m.muscleId;
                    return (
                      <button
                        key={m.muscleId}
                        onClick={() => selectMuscle(m.slug, m.side, m.viewSide)}
                        className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${isSelected
                          ? "border-brand-500 bg-brand-500 text-white shadow-sm"
                          : "border-borderPrimary bg-surfacePrimary text-textPrimary hover:bg-surfacePrimary/80 active:bg-surfacePrimary/70"
                          }`}
                      >
                        <span>{m.label} ({m.subCount})</span>
                        <span className={`text-[10px] uppercase px-1 py-0.2 rounded font-bold ${isSelected ? "bg-white/20 text-white" : "bg-surfaceSecondary text-textSecondary"
                          }`}>
                          {m.viewSide}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="hidden w-[1px] bg-borderPrimary opacity-60 lg:block"></div>

            {/* Right Side: Interactive Body Model */}
            <div className="relative min-h-[550px] flex-1">
              <div className="flex sticky top-10 flex-col items-center w-full">
                <div className="flex absolute top-0 left-0 w-full items-center justify-between pb-2">
                  <h2 className="text-lg font-semibold text-textPrimary">
                    Body Map Model ({gender === "female" ? "Female" : "Male"} - {site} View)
                  </h2>
                  {hoveredLabel && (
                    <span className="rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                      {hoveredLabel}
                    </span>
                  )}
                </div>

                <div className="relative box-border flex w-full flex-1 items-center justify-center max-h-[500px] top-16">
                  <div className="flex flex-row justify-between items-center absolute top-1/2 left-1/2 w-full box-border -translate-x-1/2 -translate-y-1/2">
                    <p className="text-[150px] font-bold text-textPrimary/10">{site === "Back" ? "L" : "R"}</p>
                    <p className="text-[150px] font-bold text-textPrimary/10">{site === "Back" ? "R" : "L"}</p>
                  </div>
                  <div
                    className="origin-top scale-[0.85]"
                    onMouseLeave={() => setHoveredLabel(null)}
                    onMouseOver={(e) => {
                      const target = e.target as SVGPathElement;
                      let id = target.id as Slug;
                      if (id === ("anus" as Slug)) id = "perineum";
                      if (id === ("female-anus" as Slug)) id = "female-perineum";
                      if (id === ("spine" as Slug)) id = "backbone";
                      const targetSide = target.getAttribute("data-side") as "left" | "right" | "common" | null;
                      const side = targetSide === "left" || targetSide === "right" ? targetSide : undefined;
                      if (id) {
                        const label = buildLabel(id, side, dbLocations as any, site, gender);
                        setHoveredLabel(label !== id ? label : null);
                      }
                    }}
                  >
                    <div style={{ transform: site === "Front" ? "scaleX(-1)" : "none" }}>
                      <Body
                        data={bodyData.filter((d) => (d.bodySide || "front") === site.toLowerCase())}
                        side={site === "Front" ? "front" : "back"}
                        gender={gender}
                        scale={1.4}
                        border="#dfdfdf"
                        onBodyPartClick={handleBodyPartClick}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Confirmation Modal for Reset Location Data */}
      {resetConfirmOpen && (
        <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-borderPrimary bg-surfacePrimary p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-textPrimary">Reset Location Data</h3>
            <p className="mt-2 text-sm text-textSecondary">
              Are you sure you want to reset all body locations? This will restore both Male and Female body models to the default isolated datasets. Any custom names or sublocations will be replaced.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                disabled={loading}
                onClick={() => setResetConfirmOpen(false)}
                className="rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 py-2 text-sm font-semibold text-textSecondary transition hover:bg-surfaceSecondary/80"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={resetAndBulkUploadLocations}
                className="flex items-center gap-1.5 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 active:bg-red-800 disabled:opacity-50"
              >
                {loading ? "Resetting..." : "Yes, Reset All"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AppSnackbar
        isOpen={snackbar.open}
        title={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
      />
    </div>
  );
};

export default Locations;

